
import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { FoodType } from "@/types/recipe";
import Library from "./Library";
import { toast } from "sonner";

const VALID_FOOD_TYPES: FoodType[] = [
  'Chicken', 'Dessert', 'Snacks', 'Vegetables',
  'Ground Beef', 'Seafood', 'Soup', 'Pork', 'Other'
];

// Convert a food type to its URL format (lowercase, spaces to hyphens)
const toUrlFormat = (foodType: string): string => {
  return foodType.toLowerCase().replace(/\s+/g, '-');
};

// Convert URL format back to valid food type
const fromUrlFormat = (urlFormat: string): string => {
  return urlFormat.replace(/-/g, ' ');
};

const isFoodType = (value: string): value is FoodType => {
  const normalized = fromUrlFormat(value.toLowerCase());
  return VALID_FOOD_TYPES.some(type => 
    type.toLowerCase() === normalized
  );
};

const getFoodType = (value: string): FoodType | null => {
  const normalized = fromUrlFormat(value.toLowerCase());
  return VALID_FOOD_TYPES.find(type => 
    type.toLowerCase() === normalized
  ) || null;
};

const FoodTypeLibrary = () => {
  const { foodType } = useParams<{ foodType: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!foodType) {
      toast.error("Invalid food type");
      navigate("/recipes");
      return;
    }

    // If it's not a valid food type at all
    if (!isFoodType(foodType)) {
      toast.error("Invalid food type");
      navigate("/recipes");
      return;
    }

    // Get the canonical URL format for this food type
    const validFoodType = getFoodType(foodType);
    if (!validFoodType) return;
    
    const canonicalUrl = toUrlFormat(validFoodType);

    // If we're not at the canonical URL, redirect to it
    if (foodType !== canonicalUrl) {
      navigate(`/recipes/${canonicalUrl}`, { replace: true });
    }
  }, [foodType, navigate]);

  if (!foodType || !isFoodType(foodType)) {
    return null;
  }

  const validFoodType = getFoodType(foodType);
  
  if (!validFoodType) {
    return null;
  }

  return <Library defaultFoodType={validFoodType} />;
};

export default FoodTypeLibrary;
